import React from 'react';
import { Badge, Link, Wrap, Text } from '@chakra-ui/react';
import { Edit, Trash2, Settings } from 'lucide-react';
import { TableColumn, TableAction, ActionHandler } from '@/components/section/EntityTableSection';
import { ConditionLookup } from './api';
import RelativeTime from '@/components/ui/RelativeTime';
import { ConditionAction } from './hooks/useConditionActions';
import { getStatusBadgeProps } from '@/utils/lookupStatus';
import { ConditionGroupLookup } from '@entities/condition-groups/api';

// Component to display condition groups in table
// Now receives groupsMap to avoid fetching data in every row
const ConditionGroupsCell: React.FC<{ 
  condition: ConditionLookup; 
  groupsMap: Record<number, ConditionGroupLookup>;
}> = React.memo(({ condition, groupsMap }) => {
  if (!condition.group_ids?.length) {
    return <Text fontSize="sm" color="gray.500">No groups</Text>;
  }

  // Map IDs to group objects using the lookup map
  const assignedGroups = condition.group_ids
    .map(id => groupsMap[id])
    .filter((group): group is ConditionGroupLookup => !!group);

  if (assignedGroups.length === 0) {
    // If IDs exist but groups aren't in map yet (loading or stale), show ID or loading
    if (Object.keys(groupsMap).length === 0) {
       return <Text fontSize="sm" color="gray.500">Loading...</Text>;
    }
    return <Text fontSize="sm" color="gray.500">Unknown groups</Text>;
  }

  return (
    <Wrap gap={1} maxW="200px">
      {assignedGroups.slice(0, 2).map((group) => (
        <Badge
          key={group.id}
          size="sm"
          colorPalette="blue"
          variant="subtle"
        >
          {group.name}
        </Badge>
      ))}
      {assignedGroups.length > 2 && (
        <Badge size="sm" colorPalette="gray" variant="subtle">
          +{assignedGroups.length - 2} more
        </Badge>
      )}
    </Wrap>
  );
});

ConditionGroupsCell.displayName = 'ConditionGroupsCell';

export const createConditionsColumns = (
  groupsMap: Record<number, ConditionGroupLookup> = {}
): TableColumn<ConditionLookup>[] => [
  {
    key: "id",
    title: "ID",
    variant: "id",
  },
  {
    key: "name",
    title: "NAME",
    variant: "primary",
  },
  {
    key: "speciality",
    title: "SPECIALITY",
    render: (condition) => (
      condition.speciality ? (
        <Link
          href={`/specialities/#/${condition.speciality.id}/view`}
          color="brand.500"
          fontSize="sm"
          fontWeight="medium"
        >
          {condition.speciality.title}
        </Link>
      ) : (
        <span>N/A</span>
      )
    ),
  },
  {
    key: "groups",
    title: "GROUPS",
    render: (condition) => <ConditionGroupsCell condition={condition} groupsMap={groupsMap} />,
  },
  {
    key: "status",
    title: "STATUS",
    render: (condition) => {
      const { colorPalette, label } = getStatusBadgeProps(condition.status);
      return (
        <Badge colorPalette={colorPalette} size="sm">
          {label}
        </Badge>
      );
    },
  },
  {
    key: "created_at",
    title: "CREATED",
    render: (condition) => (
      <RelativeTime date={condition.created_at} />
    ),
  },
  {
    key: "updated_at",
    title: "UPDATED",
    render: (condition) => (
      <RelativeTime date={condition.updated_at || condition.created_at} />
    ),
  },
];

export const createConditionsActions = (
  onAction: ActionHandler<ConditionLookup, ConditionAction>,
  isLoading: boolean
): TableAction<ConditionLookup>[] => [
  {
    key: "edit",
    label: "Edit",
    icon: Edit,
    onClick: (entity) => onAction("edit", entity),
    disabled: isLoading,
  },
  {
    key: "manage_groups",
    label: "Manage Groups",
    icon: Settings,
    onClick: (entity) => onAction("manage_groups", entity),
    disabled: isLoading,
    colorPalette: "blue",
  },
  {
    key: "delete",
    label: "Delete",
    icon: Trash2,
    onClick: (entity) => onAction("delete", entity),
    disabled: isLoading,
    colorPalette: "red",
  },
]; 